import React, { useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { COUNTING_ENGINE_THEME, countingMatchChoices, countingMatchPiece, countingSpokenWord, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import { CARD_FACE, CountingPanel, CountingPieceGrid, CountingRoot, NumberCard, PanelChip, darkenHex, hexAlpha, useCompleteOnce } from "../CountingPiece";

/**
 * Quantity <-> numeral (or number word <-> numeral). The pieces are
 * tap-to-count so the child can tag them before choosing the numeral.
 */
const MatchRepGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const target = countingTarget(activity);
  const piece = countingMatchPiece(activity);
  const accent = COUNTING_ENGINE_THEME.matchRep.accent;
  const choices = countingMatchChoices(activity);
  const wordCard = activity.number === 59 ? "thirteen" : activity.number === 79 ? "six hundred forty-five" : null;
  const [picked, setPicked] = useState(null);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const solved = picked?.ok === true;

  const choose = (value) => {
    if (solved) return;
    if (value === target) {
      setPicked({ value, ok: true });
      sfx?.("correct");
      speak?.(countingSpokenWord(value), { interrupt: true });
      setBurst((b) => b + 1);
      complete();
      return;
    }
    setPicked({ value, ok: false });
    onWrong?.();
    setTimeout(() => setPicked((p) => (p?.value === value && !p.ok ? null : p)), 600);
  };

  return (
    <CountingRoot>
      <CountingPanel grow tint={accent} chip={<PanelChip text={solved ? "That's it" : wordCard ? "Read the word" : "Count, then pick"} accent={accent} />}>
        {wordCard ? (
          <motion.div
            initial={reduceMotion ? false : { scale: 0.8, opacity: 0, rotate: -2 }}
            animate={{ scale: 1, opacity: 1, rotate: -1.5 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            style={{
              padding: "clamp(14px, 3vmin, 30px) clamp(20px, 5vmin, 48px)", borderRadius: LEARN.radius.lg, background: CARD_FACE,
              border: `3px solid ${accent}`, boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 calc(var(--tile) * 0.08) 0 ${darkenHex(accent, 0.2)}, 0 calc(var(--tile) * 0.16) calc(var(--tile) * 0.26) ${hexAlpha(LEARN.color.ink, 0.14)}`, textAlign: "center",
              fontSize: "clamp(26px, calc(var(--tile) * 0.5), 52px)", fontWeight: 700, color: LEARN.color.ink, lineHeight: 1.1, maxWidth: "100%",
            }}
          >
            {wordCard}
          </motion.div>
        ) : (
          <CountingPieceGrid
            count={target}
            asset={piece}
            countable
            speak={speak}
            sfx={sfx}
            accent={accent}
            reduceMotion={reduceMotion}
          />
        )}
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile) * 0.16)", justifyContent: "center", minHeight: "calc(var(--tile) * 1.4)", alignItems: "center" }}>
        {choices.map((value) => (
          <NumberCard
            key={value}
            accent={accent}
            reduceMotion={reduceMotion}
            disabled={solved}
            size={choices.length > 2 && String(value).length > 2 ? "md" : "lg"}
            state={picked?.value === value ? (picked.ok ? "correct" : "wrong") : "idle"}
            onClick={() => choose(value)}
          >
            {value}
          </NumberCard>
        ))}
      </div>
    </CountingRoot>
  );
};

export default MatchRepGame;
